import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Settings, Zap, Award } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import SEOHead from '../../components/seo/SEOHead';
import Breadcrumbs from '../../components/seo/Breadcrumbs';
import InternalLinks from '../../components/seo/InternalLinks';
import GoogleReviewsStrip from '../../components/GoogleReviewsStrip';

const WaterFillingMachine = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', projectType: 'Water Filling Machine' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/submit-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to submit');
      const result = await response.json();
      toast.success(result.message);
      setFormData({ name: '', phone: '', projectType: 'Water Filling Machine' });
    } catch (error) {
      toast.error('Unable to submit. Please call +91-9351292512');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Water Filling Machine — Types, Prices & Buying Guide | Alden Technologies"
        description="Compare water filling machines from 30 to 200 BPM — prices, specifications, automation levels and how to choose the right capacity for your bottling plant."
        path="/water-filling-machine"
      />
      <Breadcrumbs items={[{ label: 'Water Filling Machine Guide' }]} />
      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-to-br from-blue-900 via-indigo-700 to-purple-600">
        <div className="container mx-auto px-4 z-10 relative text-center">
          <div className="inline-block bg-purple-500/90 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            ⚙️ Automatic Water Filling Machines
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Advanced Water Filling Machines
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto">
            High-Speed Rinsing, Filling & Capping Solutions | 24 BPM to 120 BPM
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" className="bg-white text-purple-900 hover:bg-purple-50 px-8 py-7 text-lg font-semibold" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>
              Get Free Quote<ArrowRight className="ml-2" />
            </Button>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-900 px-8 py-7 text-lg font-semibold">
                <Phone className="mr-2" />Call Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our <span className="text-purple-600">Water Filling Machine</span> Range
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { id: '24-30bpm', capacity: '24-30 BPM', price: '₹8-12 Lakhs', bottles: '1440-1800/hr', color: 'emerald' },
              { id: '40-60bpm', capacity: '40-60 BPM', price: '₹15-25 Lakhs', bottles: '2400-3600/hr', color: 'blue' },
              { id: '80-120bpm', capacity: '80-120 BPM', price: '₹35-55 Lakhs', bottles: '4800-7200/hr', color: 'purple' }
            ].map((item) => (
              <Card key={item.id} className={`border-2 border-${item.color}-200 hover:shadow-2xl transition-all`}>
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-${item.color}-100 rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <Settings className={`w-8 h-8 text-${item.color}-600`} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{item.capacity}</h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Production: {item.bottles}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Price Range: {item.price}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">Fully Automatic RFC</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">SS 304 Construction</span>
                    </div>
                  </div>
                  <Link to="/contact">
                    <Button className={`w-full bg-${item.color}-600 hover:bg-${item.color}-700 text-white`}>
                      Get Quote<ArrowRight className="ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Machine <span className="text-purple-600">Features & Advantages</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Zap className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Advanced Technology</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'PLC-based automatic control system',
                    'Touch screen HMI interface',
                    'Servo motor driven precision',
                    'No bottle, no fill safety feature',
                    'Adjustable filling volume control'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <Award className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Quality & Compliance</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    'BIS certified - IS 14543 compliant',
                    '100% food grade SS 304 material',
                    'Automatic CIP cleaning system',
                    'Zero contamination design',
                    'Low maintenance & energy efficient'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section id="quote-form" className="py-16 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center mb-8">
            <h2 className="text-4xl font-bold text-white mb-4">Get Free Quote for Water Filling Machine</h2>
            <p className="text-xl text-purple-100">Expert consultation & competitive pricing - Contact us today</p>
          </div>
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto bg-white rounded-lg p-8 shadow-2xl">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <Input type="text" placeholder="Your Name *" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="py-6" disabled={isSubmitting} />
              <Input type="tel" placeholder="Phone Number *" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="py-6" disabled={isSubmitting} />
            </div>
            <Button type="submit" size="lg" className="w-full bg-purple-600 hover:bg-purple-700 text-white py-7 text-xl font-semibold" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Free Quote & Technical Details'}
              {!isSubmitting && <ArrowRight className="ml-2" />}
            </Button>
          </form>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-gray-900 to-purple-900">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Need Expert Advice on Filling Machines?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">Contact our technical team for personalized recommendations</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact"><Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-7 text-lg font-semibold">Get Detailed Quote<ArrowRight className="ml-2" /></Button></Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-purple-900 px-8 py-7 text-lg font-semibold" as="a" href="tel:+919351292512"><Phone className="mr-2" />Call: +91-9351292512</Button>
          </div>
        </div>
      </section>
      <GoogleReviewsStrip />
      <InternalLinks currentPath="/water-filling-machine" />
    </div>
  );
};

export default WaterFillingMachine;
